import { Link } from 'react-router-dom';
import { ArrowRight, Star, Shield, Heart, Wifi, Zap, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { getAppUrl } from '../config/api';
import heroBg from '../assets/hero-bg.png';
import './Home.css';

const Home = () => {
    const features = [
        {
            icon: Star,
            title: 'Premium Amenities',
            description: 'From high-speed Wi-Fi to fully equipped gyms, enjoy amenities designed for a modern lifestyle.'
        },
        {
            icon: Shield,
            title: 'Secure Environment',
            description: '24/7 security, CCTV surveillance, and biometric access to ensure your peace of mind.'
        },
        {
            icon: Heart,
            title: 'Community First',
            description: 'Join a vibrant community of like-minded individuals with regular events and gatherings.'
        }
    ];

    return (
        <div className="home">
            <SEO
                title="Hashtag PG & Flats - Premium Accommodations in Pune"
                description="Find the best PG accommodations and flats for rent in Pune. Premium amenities, great locations, and affordable prices. Book your visit today!"
                keywords="PG in Pune, flats for rent Pune, paying guest accommodation, rental properties Pune, student housing"
                url={getAppUrl()}
            />

            {/* Hero Section */}
            <section className="hero">
                <div className="hero-bg">
                    <img src={heroBg} alt="" className="hero-bg-image" />
                    <div className="hero-overlay"></div>
                </div>

                <div className="container hero-content">
                    <motion.span
                        className="hero-subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        Premium Living Spaces
                    </motion.span>

                    <motion.h1
                        className="hero-title"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Find Your <span className="hero-title-accent">Sanctuary</span>
                    </motion.h1>

                    <motion.p
                        className="hero-description"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        Experience the epitome of comfort and style with our curated collection of premium PGs and luxury flats.
                    </motion.p>

                    <motion.div
                        className="hero-cta"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                    >
                        <Link to="/listings" className="btn btn-primary btn-lg">
                            Explore Collection
                            <ArrowRight size={20} />
                        </Link>
                        <Link to="/contact" className="btn btn-white btn-lg">
                            Contact Us
                        </Link>
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <div className="hero-scroll">
                    <div className="hero-scroll-line"></div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-subtitle">Why Choose Us</span>
                        <h2 className="section-title">Elevated Living Standards</h2>
                    </div>

                    <div className="features-grid">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={index}
                                    className="feature-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                >
                                    <div className="feature-icon">
                                        <Icon size={28} />
                                    </div>
                                    <h3 className="feature-title">{feature.title}</h3>
                                    <p className="feature-description">{feature.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Lifestyle Section */}
            <section className="lifestyle section">
                <div className="container">
                    <div className="lifestyle-grid">
                        <motion.div
                            className="lifestyle-content"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="section-subtitle">The Experience</span>
                            <h2 className="lifestyle-title">
                                Designed for Comfort,<br />Built for Life.
                            </h2>
                            <p className="lifestyle-description">
                                Our properties are more than just four walls. They are thoughtfully designed spaces that inspire creativity, relaxation, and connection. Every detail is curated to enhance your daily living experience.
                            </p>
                            <Link to="/listings" className="lifestyle-link">
                                View Our Properties
                                <ArrowRight size={18} />
                            </Link>
                        </motion.div>

                        <motion.div
                            className="lifestyle-image-wrapper"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <img
                                src="https://images.unsplash.com/photo-1556912173-46c336c7fd55?w=800&auto=format&fit=crop&q=80"
                                alt="Modern living space"
                                className="lifestyle-image"
                            />
                            <div className="lifestyle-stat-box">
                                <p className="stat-number">500+</p>
                                <p className="stat-label">Happy Residents</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="cta-title">Ready to Move In?</h2>
                        <p className="cta-description">
                            Browse our exclusive collection of properties and find the one that speaks to you.
                        </p>
                        <Link to="/listings" className="btn btn-secondary btn-lg">
                            Browse Available Units
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Home;
